import * as Papa from 'papaparse';

export interface ValidationResult {
    validCount: number;
    message: string;
}

export interface ValidationError {
    message: string;
}

export interface CsvValidatorConfig {
    maxFileSizeBytes: number;
    requiredHeadings: string[];
    optionalHeadings: string[];
    validateHeadings?: (headings: string[]) => string | undefined;
    validateHeading?: (heading: string) => string | undefined;
}

export function formatFileSize(bytes: number): string {
    if (bytes >= 1000 ** 3) {
        return `${parseFloat((bytes / 1000 ** 3).toFixed(2))}GB`;
    }
    if (bytes >= 1000 ** 2) {
        return `${parseFloat((bytes / 1000 ** 2).toFixed(2))}MB`;
    }
    return `${parseFloat((bytes / 1000).toFixed(2))}KB`;
}

const ERRORS_MESSAGES = {
    encoding: (rowIndex: number) => `Invalid character at row ${rowIndex}. Make sure your CSV has UTF-8 encoding.`,
    headingEmpty: () => 'Found an empty heading.',
    headingLength: (heading: string) => `Heading "${heading}" is too long.`,
    headingUnique: (heading: string) => `Heading "${heading}" must be unique.`,
    headingRequired: (heading: string) => `Heading ${heading} is required.`,
    headingInvalid: (heading: string) =>
        `The field ${heading} could not be mapped, please refer to the example template and follow format as is in it, including case-folding.`,
    invalidColumns: (rowIndex: number) => `Wrong number of values at row ${rowIndex}.`,
    failedToProcess: (rowIndex: number, message: string) =>
        `It was not possible to process row ${rowIndex}.${message ? ` ${message}` : ''}`,
};

export interface CsvValidator {
    validate: (file: File) => Promise<ValidationResult>;
    maxFileSizeBytes: number;
}

export function createCsvValidator(config: CsvValidatorConfig): CsvValidator {
    const validate = (file: File): Promise<ValidationResult> => {
        let errorMessage = '';

        const handleError = (message: string, parser: Papa.Parser) => {
            errorMessage = message;
            parser.abort();
        };

        return new Promise((resolve, reject: (reason: ValidationError) => void) => {
            if (!file?.type || file.type !== 'text/csv') {
                reject({
                    message: 'The file is not a CSV. Please use CSV file in order to proceed with import.',
                });
                return;
            }

            if (file.size > config.maxFileSizeBytes) {
                reject({
                    message: `Max file size is ${formatFileSize(config.maxFileSizeBytes)}.`,
                });
                return;
            }

            let rowsCount = 0;
            const csvHeadings: string[] = [];

            Papa.parse(file, {
                delimiter: ',',
                worker: true,
                skipEmptyLines: true,
                step: (results: Papa.ParseStepResult<string[]>, parser: Papa.Parser) => {
                    rowsCount++;
                    if (results.errors.length) {
                        const firstError = results.errors[0];
                        handleError(
                            ERRORS_MESSAGES.failedToProcess(firstError?.row ?? rowsCount, firstError?.message ?? ''),
                            parser
                        );
                        return;
                    }

                    const data = results.data;

                    for (const value of data) {
                        if (value && value.indexOf('\uFFFD') >= 0) {
                            handleError(ERRORS_MESSAGES.encoding(rowsCount), parser);
                            return;
                        }
                    }

                    if (rowsCount === 1) {
                        for (const required of config.requiredHeadings) {
                            if (!data.includes(required)) {
                                handleError(ERRORS_MESSAGES.headingRequired(required), parser);
                                return;
                            }
                        }

                        if (config.validateHeadings) {
                            const customError = config.validateHeadings(data);
                            if (customError) {
                                handleError(customError, parser);
                                return;
                            }
                        }

                        const lcHeadingHash: Record<string, number> = {};

                        for (const heading of data) {
                            if (!heading) {
                                handleError(ERRORS_MESSAGES.headingEmpty(), parser);
                                return;
                            }

                            if (config.validateHeading) {
                                const customError = config.validateHeading(heading);
                                if (customError) {
                                    handleError(customError, parser);
                                    return;
                                }
                            }

                            if (heading.length > 80) {
                                handleError(ERRORS_MESSAGES.headingLength(heading), parser);
                                return;
                            }

                            const lcHeading = heading.toLowerCase();

                            if (Object.keys(lcHeadingHash).includes(lcHeading)) {
                                handleError(ERRORS_MESSAGES.headingUnique(heading), parser);
                                return;
                            }

                            lcHeadingHash[lcHeading] = 1;

                            if (config.optionalHeadings.indexOf(lcHeading) >= 0) {
                                if (heading !== lcHeading) {
                                    handleError(ERRORS_MESSAGES.headingInvalid(heading), parser);
                                    return;
                                }
                            }
                            csvHeadings.push(lcHeading);
                        }

                        return;
                    }

                    if (data.length !== csvHeadings.length) {
                        handleError(ERRORS_MESSAGES.invalidColumns(rowsCount), parser);
                        return;
                    }
                },
                complete: () => {
                    if (rowsCount <= 1 && !errorMessage) {
                        reject({
                            message:
                                'The CSV file does not contain any data. Please go back and make corrections to the file.',
                        });
                        return;
                    }

                    resolve({
                        validCount: rowsCount - 1,
                        message: errorMessage,
                    });
                },
                error: () => {
                    reject({
                        message: 'It was not possible to process the CSV file. Please contact support.',
                    });
                },
            });
        });
    };

    return { validate, maxFileSizeBytes: config.maxFileSizeBytes };
}
